// src/pages/trading-portal/dashboard/TradingDashboard.js
// Trading Portal dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Chip,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { AlertStatusColors, AlertStatusLabels } from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const NAV_ITEMS = [
    { label: 'Watchlist', icon: VisibilityIcon, path: '/trading-portal/watchlist', color: ACCENT, description: 'Manage stock watchlist and tracked symbols' },
    { label: 'Market Data', icon: CandlestickChartIcon, path: '/trading-portal/market-data', color: '#3b82f6', description: 'Configure and manage market data feeds' },
    { label: 'Historical Data', icon: TimelineIcon, path: '/trading-portal/historical', color: '#8b5cf6', description: 'Browse and import historical price data' },
    { label: 'Indicators', icon: AnalyticsIcon, path: '/trading-portal/indicators', color: '#10b981', description: 'Technical indicators and price analytics' },
    { label: 'Price Alerts', icon: NotificationsActiveIcon, path: '/trading-portal/alerts', color: '#ef4444', description: 'Set and manage price alerts' },
];

// Sub-components
const StatCard = ({ icon: Icon, title, value, color, subtitle, onClick }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick
                ? { borderColor: color, boxShadow: `0 4px 20px ${color}20`, transform: 'translateY(-2px)' }
                : {},
        }}
        onClick={onClick}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default function TradingDashboard() {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        total_symbols: 0,
        active_alerts: 0,
        triggered_today: 0,
        market_status: 'Closed',
    });
    const [recentAlerts, setRecentAlerts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [addingSymbol, setAddingSymbol] = useState(null);

    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/stock-trading/dashboard');
            const data = response.data?.data || response.data || {};
            setStats({
                total_symbols: data.total_symbols || 0,
                active_alerts: data.active_alerts || 0,
                triggered_today: data.triggered_today || 0,
                market_status: data.market_status || 'Closed',
            });
            setRecentAlerts(data.recent_alerts || []);
        } catch (err) {
            console.error('Failed to fetch trading dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [fetchDashboard, canView]);

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setSearching(true);
        try {
            const response = await axiosInstance.get(`/api/stock-trading/symbols/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchResults(response.data?.data || response.data?.results || []);
        } catch (err) {
            console.error('Symbol search failed:', err);
        } finally {
            setSearching(false);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleAddSymbol = async (symbol) => {
        setAddingSymbol(symbol.symbol || symbol.ticker);
        try {
            await axiosInstance.post('/api/stock-trading/symbols', {
                symbol: symbol.symbol || symbol.ticker,
                company_name: symbol.name || symbol.company_name,
                market_type: symbol.market_type || symbol.exchange || 'OTHER',
            });
            setSearchResults(prev => prev.filter(s => (s.symbol || s.ticker) !== (symbol.symbol || symbol.ticker)));
            fetchDashboard();
        } catch (err) {
            console.error('Failed to add symbol:', err);
        } finally {
            setAddingSymbol(null);
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view the Trading Dashboard.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Welcome Header with Gradient */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TrendingUpIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Stock Price Data Engine
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Monitor your watchlist, alerts, and stock price data
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* Market Status */}
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '14px', px: 2.5, py: 1.5, textAlign: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                            <PublicIcon sx={{ fontSize: 18, color: '#fff' }} />
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>
                                {loading ? '--' : stats.market_status}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 500, mt: 0.25 }}>
                            Market Status
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} action={
                        <Button color="inherit" size="small" onClick={fetchDashboard} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                            Retry
                        </Button>
                    }>
                        {error}
                    </Alert>
                )}

                {/* Stats Grid */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={ShowChartIcon}
                                title="Total Symbols"
                                value={stats.total_symbols}
                                color={ACCENT}
                                subtitle="In your watchlist"
                                onClick={() => navigate('/trading-portal/watchlist')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={NotificationsActiveIcon}
                                title="Active Alerts"
                                value={stats.active_alerts}
                                color="#3b82f6"
                                subtitle="Monitoring price"
                                onClick={() => navigate('/trading-portal/alerts')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={CheckCircleIcon}
                                title="Triggered Today"
                                value={stats.triggered_today}
                                color="#10b981"
                                subtitle="Alerts fired"
                                onClick={() => navigate('/trading-portal/alerts')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={PublicIcon}
                                title="Market Status"
                                value={stats.market_status}
                                color={stats.market_status === 'Open' ? '#10b981' : '#94a3b8'}
                                subtitle="Current session"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Sub-Modules Navigation */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Bottom Grid: Quick Search + Recent Alerts */}
                <Grid container spacing={3}>
                    {/* Quick Add Symbol */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SearchIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Quick Add Symbol
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/watchlist')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View Watchlist
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2.5, pb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search symbol or company name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searching ? (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={18} />
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                        sx={{ mb: 1.5 }}
                                    />

                                    {searchResults.length > 0 ? (
                                        <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                            {searchResults.map((result, index) => (
                                                <ListItem key={index} sx={{ borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                                                    <ListItemText
                                                        primary={
                                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                                {result.symbol || result.ticker}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                                {result.name || result.company_name}
                                                            </Typography>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            onClick={() => handleAddSymbol(result)}
                                                            disabled={addingSymbol === (result.symbol || result.ticker)}
                                                            sx={{ color: ACCENT }}
                                                        >
                                                            {addingSymbol === (result.symbol || result.ticker) ? (
                                                                <CircularProgress size={18} />
                                                            ) : (
                                                                <AddIcon />
                                                            )}
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : searchQuery && !searching ? (
                                        <Box sx={{ py: 3, textAlign: 'center' }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                Press Enter to search for symbols
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Alerts */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <NotificationsActiveIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Alerts
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/alerts')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : recentAlerts.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <NotificationsActiveIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent alerts
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Set up price alerts to get notified
                                            </Typography>
                                        </Box>
                                    ) : (
                                        recentAlerts.slice(0, 5).map((alert, index) => (
                                            <Box
                                                key={alert.id || index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    py: 1.5,
                                                    px: 0.5,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 42, height: 42, borderRadius: '10px',
                                                    bgcolor: alert.condition === 'ABOVE' || alert.condition === 'CROSSES_ABOVE' ? '#dcfce7' : '#fee2e2',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {alert.condition === 'ABOVE' || alert.condition === 'CROSSES_ABOVE' ? (
                                                        <ArrowUpwardIcon sx={{ fontSize: 22, color: '#10b981' }} />
                                                    ) : (
                                                        <ArrowDownwardIcon sx={{ fontSize: 22, color: '#ef4444' }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                        {alert.symbol || 'Unknown'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                                        {alert.message || `Target: $${alert.target_price}`}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={AlertStatusLabels[alert.status] || alert.status}
                                                    size="small"
                                                    color={AlertStatusColors[alert.status] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                                {alert.triggered_at && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                            {new Date(alert.triggered_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
